/// <amd-module name="Artifacts/PackageRecycleBin/services/PackageRecycleBinService" />
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */

import * as Res from "Artifacts/PackageRecycleBin/Resources";
import { IDeletedPackageVersion } from "Artifacts/PackageRecycleBin/Types/";
import * as Constants from "Artifacts/Root/Constants";
import { FeedServiceInstanceId, Protocol } from "Artifacts/Root/Constants";
import { loadData } from "Artifacts/Root/helpers/DataProviderHelpers";
import { IFeed } from "Artifacts/Root/types";
import { DeletedPackageVersion } from "Artifacts/Root/types/AzureArtifacts";
import { IFeedRestClient, getFeedClient } from "Artifacts/Root/web-apis/RestClient/Feed";
import * as OperationRestClientGetter from "TFS/Admin/Common/RestClient/Operations";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService.Types";
import * as StringUtils from "VSS/Core/Util/String";
import {
  IVssPageContext,
  IVssRestClientOptions,
  IVssService,
  Services,
  VssService,
} from "VSS/Platform/Context";
import * as Operations from "VSS/Platform/Contracts/Operations";

export interface IPackageRecycleBinService extends IVssService {
  getRecycleBinPackageVersions(
    context: IVssPageContext,
    feed: IFeed,
  ): Promise<IDeletedPackageVersion[] | undefined>;
  emptyRecycleBin(pageContext: IVssPageContext, feed: IFeed): Promise<void>;
}

export class PackageRecycleBinService extends VssService implements IPackageRecycleBinService {
  feedRestClient: IFeedRestClient;

  public async getRecycleBinPackageVersions(
    context: IVssPageContext,
    feed: IFeed,
  ): Promise<IDeletedPackageVersion[]> {
    const data = await loadData<DeletedPackageVersion[]>(
      context,
      "ms.azure-artifacts.package-recycle-bin-data-provider",
      { feedId: feed.id, projectName: feed.project?.name },
      false,
      Constants.CommonConstants.FeedServiceInstanceId,
    );

    // data provider returns empty array when there are no recycle bin package
    // and returns null if the feed is deleted.
    return (
      data?.map((pkg) => {
        return {
          name: pkg.name,
          packageId: pkg.id,
          version: pkg.version,
          //in this case, we don't care about normalized version
          normalizedVersion: pkg.version,
          protocol: pkg.protocol as Protocol,
          deletedDate: pkg.deletedDate,
          scheduledPermanentDeleteDate: pkg.scheduledPermanentDeleteDate,
          views: [], // views not needed
          isDeleted: true,
        };
      }) || []
    );
  }

  /**
   * Call the empty recycle bin API, wait for job status response, and create a toast on success
   */
  public async emptyRecycleBin(pageContext: IVssPageContext, feed: IFeed): Promise<void> {
    const messageService = this.pageContext.getService<IGlobalMessagesService>(
      "IGlobalMessagesService",
    );
    const opReference = await this.callEmptyRecycleBin(pageContext, feed);
    messageService.addToast({ message: Res.RecycleBin_SuccessToast, duration: 4000 });
  }

  private callEmptyRecycleBin(
    pageContext: IVssPageContext,
    feed: IFeed,
  ): Promise<Operations.OperationReference> {
    const feedRestClient = getFeedClient(pageContext);
    return feedRestClient.emptyRecycleBin(feed.id, feed.project?.id);
  }

  // this method is intend to track the status of current running empty rb job.  Currently the UI does not have the feature
  // to show the status of a package being deleted.  Such feature should/will be implemented in the future, by then this method will be useful.
  private startPollingForJobStatus(opReference: Operations.OperationReference): Promise<void> {
    const operationRestClient = this.pageContext.getRestClient<OperationRestClientGetter.IOperationsRestClient>(
      "IOperationsRestClient",
      {
        serviceInstanceType: FeedServiceInstanceId,
      } as IVssRestClientOptions,
    );

    const isDialogOpen = () => {
      return true;
    };

    return new Promise<void>((resolve, reject) => {
      const intervalJob = setInterval(checkOperationStatus, 3000);

      async function checkOperationStatus() {
        try {
          if (!isDialogOpen()) {
            clearInterval(intervalJob);
            resolve();
            return;
          }

          const operation = await operationRestClient.getOperation(opReference.id);
          switch (operation?.status) {
            case Operations.OperationStatus.NotSet:
            case Operations.OperationStatus.Queued:
            case Operations.OperationStatus.InProgress:
              break;

            case Operations.OperationStatus.Succeeded:
              clearInterval(intervalJob);
              if (operation.resultMessage != null) {
                const resultMessage = JSON.parse(operation.resultMessage);
                // Operation API returns success for partial success.
                // If one protocol failed, there was a failure.
                if (resultMessage?.FailedProtocols?.length > 0) {
                  reject(
                    new Error(
                      StringUtils.format(
                        Res.RecycleBin_EmptyRecycleBinFailed,
                        resultMessage.FailedProtocols,
                      ),
                    ),
                  );
                  break;
                }
              }
              resolve();
              break;

            case Operations.OperationStatus.Failed:
            case Operations.OperationStatus.Cancelled:
              clearInterval(intervalJob);
              reject(new Error(Res.RecycleBin_JobWasFailedOrCanceled));
              break;
          }
        } catch (err) {
          clearInterval(intervalJob);
          reject(err.message);
        }
      }
    });
  }
}

Services.add("IPackageRecycleBinService", { serviceFactory: PackageRecycleBinService });
